import React, { useEffect, useState } from 'react';
import { cssMap as cssMapUnbounded } from '@compiled/react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { FeedbackModal } from '../FeedbackModal/FeedbackModal.tsx';
import { submitSurveyFeedback } from '../submit-feedback.ts';

const LOCAL_STORAGE_CONTENTID_LIMIT = 200; // 30 bytes per contentId, 200 is 6KB

const IMPORT_SOURCE_NOTION = 'Notion';

const translations = defineMessages({
	bannerText: {
		id: 'import-feedback.banner-text',
		defaultMessage: 'This content is imported. How does it look?',
		description:
			'Text on banner describing that the content below it is imported from a third party and asking for feedback',
	},
});

const styles = cssMap({
	root: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

const unboundedStyles = cssMapUnbounded({
	root: {
		width: 'var(--full-cover-image-width, 100%)',
		marginLeft: 'var(--full-cover-image-margin, 0px)',
		position: 'relative',
	},
});

interface ImportFeedbackBannerProps {
	importSource: string;
	importDate: Date;
	contentId: string;
}

export const ImportFeedbackBanner = ({
	importSource,
	importDate,
	contentId,
}: ImportFeedbackBannerProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isModalOpen, setIsModalOpen] = useState(false);
	//TODO (@rwang): Refactor this to use the ImportFeedbackStorageManager (CCIMPORTER-509)
	const [bannerDisabledContentIds, setBannerDisabledContentIds] = useState<string[]>(
		() =>
			JSON.parse(
				localStorage.getItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_IMPORT_FEEDBACK_DISABLED),
			) || [],
	);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'importFeedbackBanner',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const handleFeedbackClick = () => {
		setIsModalOpen(true);
	};

	//TODO (@rwang): Refactor this to use the ImportFeedbackStorageManager (CCIMPORTER-509)
	const handleDismissClick = () => {
		if (bannerDisabledContentIds.length >= LOCAL_STORAGE_CONTENTID_LIMIT) {
			bannerDisabledContentIds.shift();
		}
		const updatedIds = [...bannerDisabledContentIds, contentId];
		localStorage.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_IMPORT_FEEDBACK_DISABLED,
			JSON.stringify(updatedIds),
		);
		setBannerDisabledContentIds(updatedIds);
	};

	const formattedSource = importSource && IMPORT_SOURCE_NOTION; // Hardcoded with Notion until backend is updated

	const isFeedbackDisabled = bannerDisabledContentIds.includes(contentId);

	if (isFeedbackDisabled) {
		return <></>;
	}

	return (
		<FlagsProvider>
			<div data-vc="import-feedback-banner" css={unboundedStyles.root}>
				{/* eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage */}
				<Box xcss={cx(styles.root)} backgroundColor="color.background.accent.purple.subtler">
					<Flex justifyContent="space-between" alignItems="center">
						<Box /> {/* flexbox spacer */}
						<Flex gap="space.400" alignItems="center">
							<Text weight="medium">
								<FormattedMessage {...translations.bannerText} />
							</Text>
							<Flex gap="space.100" alignItems="center">
								<IconButton
									icon={ThumbsUpIcon}
									label="Thumbs Up"
									testId="import-feedback-banner-thumbs-up-button"
									onClick={() => {
										submitSurveyFeedback(
											createAnalyticsEvent,
											'importedPageFeedbackBanner',
											formattedSource,
											importDate,
											contentId,
											'positive',
										);
										createAnalyticsEvent({
											type: 'sendUIEvent',
											data: {
												action: 'clicked',
												actionSubject: 'thumsUpButton',
												actionSubjectId: 'importFeedbackSurvey',
												source: 'importedPageFeedbackBanner',
											},
										}).fire();
										handleDismissClick();
									}}
								/>
								<IconButton
									icon={ThumbsDownIcon}
									label="Thumbs Down"
									testId="import-feedback-banner-thumbs-down-button"
									onClick={() => {
										handleFeedbackClick();
										createAnalyticsEvent({
											type: 'sendUIEvent',
											data: {
												action: 'clicked',
												actionSubject: 'thumsDownButton',
												actionSubjectId: 'importFeedbackSurvey',
												source: 'importedPageFeedbackBanner',
											},
										}).fire();
									}}
								/>
							</Flex>
						</Flex>
						<IconButton
							appearance="subtle"
							icon={CrossIcon}
							label="Close"
							testId="import-feedback-banner-close-button"
							onClick={() => {
								createAnalyticsEvent({
									type: 'sendUIEvent',
									data: {
										action: 'clicked',
										actionSubject: 'dismissButton',
										actionSubjectId: 'importFeedbackSurvey',
										source: 'importedPageFeedbackBanner',
									},
								}).fire();
								handleDismissClick();
							}}
						/>
					</Flex>
				</Box>
				<FeedbackModal
					isOpen={isModalOpen}
					closeModal={() => setIsModalOpen(false)}
					importSource={formattedSource}
					importDate={importDate}
					contentId={contentId}
					onFeedbackSubmit={handleDismissClick}
				/>
			</div>
		</FlagsProvider>
	);
};
